import { Icon, Tooltip } from '@assembly-js/design-system';
import type { IconType as AssemblyIconType } from '@assembly-js/design-system';
import { ListItemIcon, ListItemText } from '@mui/material';
import type { ListItemButtonProps } from '@mui/material/ListItemButton';
import clsx from 'clsx';
import React, { PropsWithChildren, useContext } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  PortalConfigContext,
  SidebarTheme,
  SidebarThemeContext,
  useFeatureFlags,
} from 'src/context';

import {
  defaultListItemStyles,
  DynamicListItem,
  newSidebarListItemStyles,
} from './DynamicListItem';

const useStyles = makeStyles<SidebarTheme>()(
  (_theme, { sidebarTextColor, sidebarItemHoverBackground }) => ({
    listItemIconContainer: {
      color: sidebarTextColor,
    },
    listItemIconStrokeWidth: {
      '& path': {
        strokeWidth: 2,
      },
    },
    listItemText: {
      color: sidebarTextColor,
    },
    listItem: {
      '&:hover': {
        backgroundColor: sidebarItemHoverBackground,
      },
      '&:hover svg': {
        color: sidebarTextColor,
      },
      '&:focus': {
        background: 'transparent',
      },
    },
    selected: {
      backgroundColor: `${sidebarItemHoverBackground} !important`,
    },
  }),
);

export interface SidebarListItemIconProps {
  className?: string;
  iconName?: string;
  iconSettings?: {
    ignoreStrokeWidth?: boolean;
    className?: string;
  };
}

export const SidebarListItemIcon = ({
  className,
  iconName,
  iconSettings = {},
}: SidebarListItemIconProps) => {
  const sidebarTheme = useContext(SidebarThemeContext);
  const { classes } = useStyles(sidebarTheme);

  if (!iconName) {
    return null;
  }
  return (
    <ListItemIcon
      classes={{
        root: clsx('h-auto', classes.listItemIconContainer, className),
      }}
    >
      <Icon
        icon={iconName as AssemblyIconType}
        // Scale on height so a glyph whose viewBox is wider than it is tall
        // (signature is 640x512) isn't shrunk to fit the square's width and
        // left reading ~20% smaller than the square icons beside it. Capped
        // two pixels over so a wide glyph gains weight without gaining bulk.
        className={clsx(
          'h-[14px] w-auto max-w-[16px]',
          iconSettings?.className,
        )}
      />
    </ListItemIcon>
  );
};

export const SidebarListItemText = ({
  className,
  text,
  bold = false,
}: {
  className?: string;
  text?: string;
  bold?: boolean;
}) => {
  const sidebarTheme = React.useContext(SidebarThemeContext);
  const { classes } = useStyles(sidebarTheme);
  const { EnableNewSidebar } = useFeatureFlags();
  const isBold = bold && !EnableNewSidebar;
  return (
    <ListItemText
      primary={text}
      classes={{
        root: clsx('my-0.5', className),
        primary: clsx(
          'text-[13px] truncate',
          classes.listItemText,
          isBold ? 'leading-6 font-medium' : 'leading-5',
        ),
      }}
    />
  );
};

interface SidebarListItemContainerProps {
  htmlId: string;
  selected?: boolean;
  handleClick?: ListItemButtonProps['onClick'];
  tooltipTitle?: React.ReactNode;
  disabled?: boolean;
}

const internalSidebarListItemStyles =
  'bg-[#F8F9FB] hover:bg-gray-150 [&:hover_svg]:text-[#212B36] active:bg-[#E9EBEE] focus-visible:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#003DA5] focus-visible:-outline-offset-2';
const internalSidebarSelectedStyles = '!bg-[#E9EBEE]';
export const SidebarListItemContainer = ({
  htmlId,
  selected = false,
  handleClick,
  children,
  tooltipTitle,
  disabled,
}: PropsWithChildren<SidebarListItemContainerProps>) => {
  const sidebarTheme = React.useContext(SidebarThemeContext);
  const { classes } = useStyles(sidebarTheme);
  const { EnableNewSidebar } = useFeatureFlags();
  const portalConfig = React.useContext(PortalConfigContext);
  const isClient =
    portalConfig.viewMode === 'client' || sidebarTheme.isClientPreview;

  return (
    <Tooltip
      tooltipClassname="h-6"
      content={tooltipTitle}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      baseOffset={0}
      offset={{ x: 14, y: 0 }}
      showDelayDuration={100}
    >
      <DynamicListItem
        disabled={disabled}
        htmlId={htmlId}
        data-testid={htmlId}
        className={clsx(
          // Lets children whose background matches the row's hover background
          // (e.g. the settings upgrade badge) react to it.
          'group',
          EnableNewSidebar ? newSidebarListItemStyles : defaultListItemStyles,
          EnableNewSidebar && !isClient
            ? internalSidebarListItemStyles
            : classes.listItem,
          selected &&
            (EnableNewSidebar && !isClient
              ? internalSidebarSelectedStyles
              : classes.selected),
        )}
        onClick={handleClick}
        button
      >
        {children}
      </DynamicListItem>
    </Tooltip>
  );
};

export interface SidebarListItemProps
  extends SidebarListItemContainerProps,
    SidebarListItemIconProps {
  label?: string;
}

export const SidebarListItem = ({
  selected = false,
  label,
  iconName,
  children,
  iconSettings,
  ...props
}: PropsWithChildren<SidebarListItemProps>) => (
  <SidebarListItemContainer selected={selected} {...props}>
    <SidebarListItemIcon
      className="min-w-6"
      iconSettings={iconSettings}
      iconName={iconName}
    />
    <SidebarListItemText text={label} bold={selected} />
    {children}
  </SidebarListItemContainer>
);
